import { Alert, Box, Stack, Typography, useTheme } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useMemo } from 'react';
import useSWR from 'swr';
import { webhooks } from '@/api/platform';
import { DataTable } from '@/components/DataTable';
import { MetricStat } from '@/components/MetricStat';
import { StatusPill } from '@/components/StatusPill';
import { PageShell } from '@/layout/PageShell';
import { redactMiddle, relativeTime } from '@/utils/format';
import type { WebhookSubscription } from '@/types/backend';

/**
 * PR-7 didn't expose a "what's the secret cipher status?" endpoint, so
 * this page is intentionally narrow: it surfaces the kekId distribution
 * across webhook subscriptions (the only secret material we encrypt
 * today) and flags the warning state — any row whose kekId is the
 * sentinel "plaintext" value is running unencrypted.
 *
 * Operators see this as a "rotation status" view: green when every
 * subscription is on the active KEK, warning otherwise.
 *
 * A first-class /admin/secrets/status endpoint is queued as a follow-up
 * to PR-7; this page is honest about that.
 */

interface KekRow {
  kekId: string;
  count: number;
  isPlaintext: boolean;
}

export function SecretsPage() {
  const theme = useTheme();
  const { data: subs, error, isLoading } = useSWR(
    '/admin/webhooks/subscriptions',
    () => webhooks.subscriptions(),
  );

  const distribution = useMemo<KekRow[]>(() => {
    if (!subs) return [];
    const map = new Map<string, KekRow>();
    subs.forEach((s) => {
      const kekId = s.secretId ?? '<missing>';
      const existing = map.get(kekId);
      if (existing) existing.count += 1;
      else map.set(kekId, { kekId, count: 1, isPlaintext: kekId === 'plaintext' });
    });
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [subs]);

  const totals = useMemo(() => {
    if (!subs) return { total: 0, plaintext: 0, encrypted: 0, missing: 0 };
    let plaintext = 0;
    let encrypted = 0;
    let missing = 0;
    subs.forEach((s) => {
      if (!s.secretId) missing++;
      else if (s.secretId === 'plaintext') plaintext++;
      else encrypted++;
    });
    return { total: subs.length, plaintext, encrypted, missing };
  }, [subs]);

  return (
    <PageShell
      kicker="PLATFORM · SECRETS"
      title="Secrets"
      subtitle="Webhook subscription HMAC secrets are encrypted at rest under a KEK rotation scheme. This page surfaces what's live."
      width="wide"
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <MetricStat label="Subscriptions" value={totals.total} />
        <MetricStat label="Encrypted" value={totals.encrypted} tone="success" hint="under an active KEK" />
        <MetricStat
          label="Plaintext"
          value={totals.plaintext}
          tone={totals.plaintext > 0 ? 'danger' : 'success'}
          hint={totals.plaintext > 0 ? 'rotate via WebhookSecretAccess.rotateIfNeeded' : 'none — clean'}
        />
        <MetricStat
          label="Missing kekId"
          value={totals.missing}
          tone={totals.missing > 0 ? 'warning' : 'success'}
          hint="legacy rows pre-PR-7"
        />
      </Box>

      {totals.plaintext > 0 && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: theme.radius.sm + 'px' }}>
          {totals.plaintext} subscription{totals.plaintext === 1 ? ' is' : 's are'} running with the <code>plaintext</code> sentinel KEK.
          Verify <code>security.secret-cipher.mode=aesgcm</code> is set, then trigger lazy rotation by exercising each subscription
          (e.g. send a test event) — <code>WebhookSecretAccess</code> migrates rows on first read.
        </Alert>
      )}

      <Stack spacing={3}>
        <Box>
          <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>
            KEK distribution
          </Typography>
          <DataTable<KekRow>
            rows={distribution}
            rowKey={(r) => r.kekId}
            isLoading={isLoading}
            error={error ? { message: (error as Error).message } : null}
            empty={{ icon: LockIcon, title: 'No subscriptions yet.', description: 'KEK distribution will populate once webhook subscriptions are registered.' }}
            columns={[
              {
                header: 'KEK ID',
                cell: (r) => (
                  <Typography variant="body2" sx={{ fontFamily: theme.typography.fontFamily, fontWeight: 500 }}>
                    {r.kekId}
                  </Typography>
                ),
              },
              {
                header: 'Subscriptions',
                align: 'right',
                cell: (r) => r.count.toLocaleString(),
                width: 140,
              },
              {
                header: 'Status',
                cell: (r) => r.isPlaintext
                  ? <StatusPill tone="danger">Plaintext fallback</StatusPill>
                  : <StatusPill tone="success">Encrypted</StatusPill>,
                width: 200,
              },
            ]}
          />
        </Box>

        <Box>
          <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>
            Subscriptions on plaintext fallback
          </Typography>
          <DataTable<WebhookSubscription>
            rows={subs?.filter((s) => s.secretId === 'plaintext') ?? []}
            rowKey={(r) => r.id}
            empty={{
              icon: LockIcon,
              title: 'No plaintext subscriptions.',
              description: 'Every registered subscription has its secret encrypted under a real KEK.',
            }}
            columns={[
              { header: 'Tenant', cell: (r) => redactMiddle(r.tenantId, 6, 6), width: 160 },
              {
                header: 'URL',
                cell: (r) => (
                  <Typography variant="caption" sx={{ fontFamily: theme.typography.fontFamily, wordBreak: 'break-all' }}>{r.url}</Typography>
                ),
              },
              {
                header: 'Updated',
                cell: (r) => <Typography variant="caption" color="text.secondary">{relativeTime(r.updatedAt)}</Typography>,
                width: 140,
              },
            ]}
          />
        </Box>
      </Stack>

      <Alert severity="info" sx={{ mt: 3 }}>
        A first-class <code>/admin/secrets/status</code> endpoint that surfaces the KEK config (active key, retired keys still loaded, total secrets per KEK) is queued as a follow-up to PR-7. This page is best-effort using what's already exposed.
      </Alert>
    </PageShell>
  );
}
